import {Board, Question, QuestionStatus} from "../javaTypes";
import {Button, Tab, Tabs} from "react-bootstrap";
import {t} from "ttag";
import QuestionAnswer from "./QuestionAnswer";
import ReferenceProvider from "./ReferenceProvider";
import './BoardHome.css'
import AdvancedView from "./AdvancedView";
import MiniSearch from 'minisearch'
import {useCallback, useEffect, useMemo, useState} from "react";
import {useDebouncedEffect} from "first-common/utils";
import {messageDialog} from "first-common/dialogs";
import SeasonLogo from "../SeasonLogo";


export function SearchBox({doSearch} : {doSearch: (search: string) => void}) {
    const [search, setSearch] = useState<string>('');
    useDebouncedEffect(() => doSearch(search), [search], 200);

    return <div className={"searchLabel input-group"}>
        <input className={"form-control"} value={search} placeholder={t`Search by question title, content, or answer...`} onChange={e => setSearch(e.target.value)}/>
        <Button variant="secondary" onClick={() => setSearch('')}><i className="fa fa-close"></i></Button>
    </div>
}

export default function BoardHome({board, isAsker, isAdmin, isGK, isAnswerer, questions, followedQuestions: initialFollowedQuestions, feedUrl} : {board: Board, isAsker: boolean, isAdmin: boolean, isGK: boolean, isAnswerer: boolean, questions: Question[], followedQuestions: string[], feedUrl: string}) {
    const [followedQuestions, setFollowedQuestions] = useState(initialFollowedQuestions)
    const toggleFollow = useCallback((questionId: string, questionNumber: number) => {
        setFollowedQuestions(prev => {
            const isRemoval = prev.includes(questionId)
            fetch(`/boards/${board.season.cmpYear}/${board.season.program}/${board.slug}/qa/${questionNumber}/follow`, { method: isRemoval ? 'DELETE' : 'PUT' })
                .then(r => {
                    if(!r.ok) {
                        throw `Favorite update failed: ${r.status}`
                    }
                })
                .catch(ex => {
                    console.error(isRemoval ? "Failed to remove favorite" : "Failed to add favorite", ex)
                    messageDialog({
                        label: isRemoval ? t`Failed to remove favorite` : t`Failed to add favorite`,
                        text: <span dangerouslySetInnerHTML={{__html: t`An error occurred. Please try again later, or contact <i>FIRST</i> if this keeps occurring.`}} />
                    })
                    // Undo the mutation so frontend state matches backend
                    setFollowedQuestions((prev) => {
                        if (isRemoval) {
                            return [...prev, questionId];
                        } else {
                            return prev.filter(id => id !== questionId);
                        }
                    })
                })
            if (isRemoval) {
                return prev.filter(id => id !== questionId);
            } else {
                return [...prev, questionId];
            }
        });
    }, [setFollowedQuestions]);

    const mapQuestion = useCallback((question: Question) => <QuestionAnswer key={question.questionId} board={board} question={question} canFollowUp={isAsker} loadedQuestions={questions}
                                                                linkHeader={true} canEditAnswer={false} canEditQuestion={false} editing={false} isFollowing={window.FIRST_ENV.userAccountId ? followedQuestions.includes(question.questionId) : undefined}
                                                                toggleFollow={() => toggleFollow(question.questionId, question.number)}
                                                                />, [board, isAsker, questions, followedQuestions]);

    const [filterResults, setFilterResults] = useState(questions);
    const miniSearch = useMemo(() => {
        return new MiniSearch({
            fields: ['title', 'content', 'answer.content', 'topic', 'rule'], // fields to index for full-text search
            idField: 'number',
            extractField: (document, fieldName) => {
                // Access nested fields
                return fieldName.split('.').reduce((doc, key) => doc && doc[key], document)
            }
        });
    }, []);
    useEffect(() => {
        miniSearch.removeAll();
        miniSearch.addAll(questions);
    }, [questions]);

    return <div>
        <div className={"text-center standardHeader"}>
            <div className={"d-flex justify-content-between align-items-center"}>
                <div style={{width: "10em"}}>&nbsp;</div>
                <SeasonLogo program={board.season.program} cmpYear={board.season.cmpYear} />
                <div style={{width: "10em"}} className={"d-flex justify-content-end ms-2"}>{isAsker ? <Button variant={'primary'} href={`${board.slug}/ask`}>{t`My Questions`}</Button> : null}</div>
            </div>
            <div className={"d-flex justify-content-between align-items-center mb-2"}>
                <div style={{width: "1.5em"}}>&nbsp;</div>
                <h1>{board.name} Q&A ({board.season.yearString})</h1>
                {board.config.enableRSS ?
                    <a href={feedUrl} className={"text-white rounded"} style={{width: "1.5em", background: "#f26522"}}><i className={"fa fa-rss"} title="Feed of answered questions"></i></a> :
                    <div style={{width: "1.5em"}}>&nbsp;</div>
                }
            </div>
            <div className={"justify-content-center d-flex gap-2 mb-3"}>
                {isAdmin ? <Button variant={"secondary"} href={`${board.slug}/settings`}>{t`Board Settings`} <i className={"fa fa-gear"}/></Button> : null}
                {isAnswerer ? <Button variant={"primary"} href={`${board.slug}/answer`}>{t`Manage Answers`}</Button> : null}
            </div>
            <hr/>
        </div>

        <div id={"printHeader"} className={"text-center"}>
            <h6 style={{marginTop: 0, marginBottom: 0}}>{board.season.yearString} {board.season.gameName} {board.name} Q&A. Generated {new Date().toLocaleString()}</h6>
        </div>

        <ReferenceProvider board={board}>
            <SearchBox doSearch={(search) => {
                if (search.length <= 3) {
                    setFilterResults(questions);
                } else {
                    let searchResults = new Set<number>(miniSearch.search(search, {prefix: true}).map(x => x.id));
                    setFilterResults(questions.filter(q=> searchResults.has(q.number)));
                }
            }} />
            <Tabs style={{justifyContent: 'center'}} className={"mt-1"}>
                <Tab title={t`Answer Feed`} eventKey={"answered"}><br/>
                    {filterResults.filter(q => q.status === QuestionStatus.ANSWERED).map(mapQuestion)}
                    {board.config.enableRSS ? <a href={feedUrl}>{t`RSS feed of answered questions`}</a> : null}
                </Tab>
                {board.config.viewUnanswered ?
                    <Tab title={t`Unanswered`} eventKey={"unanswered"}><br/>
                        {filterResults.filter(q => q.status === QuestionStatus.ACCEPTED).map(mapQuestion)}
                    </Tab> : null
                }
                {window.FIRST_ENV.userAccountId ? <Tab title={t`Followed`} eventKey={"followed"}><br/>
                    {filterResults.filter(q => followedQuestions.includes(q.questionId)).map(mapQuestion)}
                    {filterResults.filter(q => followedQuestions.includes(q.questionId)).length == 0 ? <p>
                        {t`You are not currently following any questions. Click the star next to any question to follow it.`}
                    </p> : null}
                </Tab> : null}
                <Tab title={t`Additional Views`} eventKey={"advanced"} tabClassName={"d-none d-sm-block"}><br/>
                    <AdvancedView board={board} questions={filterResults} renderQuestion={mapQuestion}/>
                </Tab>
            </Tabs>
        </ReferenceProvider>
    </div>
}

